import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import ErrorPage from "@/pages/-error";
import toast from "react-hot-toast";
import { useDocumentTitle } from "@/hooks/useDocumentTitle.ts";
import { ShieldAlert } from "lucide-react";
import { Button, Input, Label, Link, Spinner, TextField } from "@heroui/react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { asSearchString } from "@repo/utils/search-params";
import { useSubmitRealNameWithParam } from "@/service/api/api-with-uid";

export type F2AIdSearch = {
    biz?: string;
    redirect?: string;
    uid?: string;
};

export const Route = createFileRoute("/f2a/id")({
    validateSearch: (search: Record<string, unknown>): F2AIdSearch => ({
        biz: asSearchString(search.biz),
        redirect: asSearchString(search.redirect),
        uid: asSearchString(search.uid),
    }),
    component: OTP,
});


const routeApi = getRouteApi("/f2a/id");

export default function OTP() {
    const navigate = useNavigate();
    const { biz, redirect, uid: uidParam } = routeApi.useSearch();
    const [realName, setRealName] = useState<string>("");
    const [idNumber, setIdNumber] = useState<string>("");
    const [submitLoading, setSubmitLoading] = useState(false);

    // 使用支持URL参数UID的API工具
    const { submitRealName, uid: contextUid } = useSubmitRealNameWithParam();
    const uid = uidParam || contextUid;

    useDocumentTitle("实名信息验证");

    const handleSubmit = useCallback(async () => {
        if (realName.length === 0 || idNumber.length === 0) {
            toast.error("请输入真实姓名和身份证号");
            return;
        }
        if (!biz) {
            toast.error("参数无效");
            return;
        }

        try {
            setSubmitLoading(true);

            // 使用优化的API调用，支持URL参数UID
            const { data } = await submitRealName(uidParam, realName, idNumber, biz);

            if (!data.session_code) return;
            navigate({ href: `${redirect}?session_code=${data.session_code}&uid=${uid}`, replace: true });
        } catch (err: any) {
            toast.error(err.msg || '提交失败');
        } finally {
            setSubmitLoading(false);
        }
    }, [realName, idNumber, biz, navigate, redirect, uidParam, submitRealName, uid]);

    if (!redirect || !biz) {
        return <ErrorPage error={{ msg: "参数无效", request_id: "FURRIES.PARAMS.INVALID" }} />;
    }

    return (
        <div className={"flex flex-col bg-background h-screen"}>
            <section className={"flex flex-col items-center gap-2 p-8 bg-white"}>
                <div className="p-4 rounded-full bg-green-200/30 mb-4"><ShieldAlert size={100} className="text-green-500/60" strokeWidth={1.5} /></div>
                <span className={"mb-2 text-md"}>为了识别您的身份，我们需要您提供一些信息</span>
                <span className={"mb-2 text-sm text-red-500"}>非大陆证件请使用其他方式验证</span>
            </section>
            <div className="flex flex-col gap-4 px-4 mt-3">
                <TextField
                    name="realName"
                    fullWidth
                    isDisabled={submitLoading}
                    value={realName}
                    onChange={setRealName}
                >
                    <Label>真实姓名</Label>
                    <Input placeholder="请输入真实姓名" />
                </TextField>
                <TextField
                    name="idNumber"
                    fullWidth
                    isDisabled={submitLoading}
                    value={idNumber}
                    onChange={setIdNumber}
                >
                    <Label>证件号码</Label>
                    <Input placeholder="请输入身份证号" />
                </TextField>
            </div>
            <section className={"flex flex-col items-center p-4 mt-1 gap-3"}>
                <Button
                    className="w-full rounded-full"
                    onPress={handleSubmit}
                    isPending={submitLoading}
                    isDisabled={submitLoading || idNumber.length < 18 || realName === ""}
                >
                    {({ isPending }) => (
                        <>
                            {isPending && <Spinner size="sm" color="current" />}
                            确认
                        </>
                    )}
                </Button>
                <Link
                    className="text-sm text-primary no-underline"
                    onPress={() => navigate({ to: "/f2a", search: { biz, redirect }, replace: true })}
                >
                    我无法通过这个方式验证
                </Link>
            </section>
        </div>
    );
}
